import { each, get, isEmpty, mapValues } from "lodash";
import validate from "validate.js";
import listingValidation from "../../listing/ducks/validation";
import { validateFieldWithKey } from "../../utils/findAndValidateField";
import { constraints } from "../../utils/validation";

const compConstraints = {
    Office: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Industrial: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.sfAvail": {
            ...constraints.number,
        },
        "listing.attributes.largestContigSf": {
            ...constraints.number,
        },
        "listing.attributes.officeSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.yardSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.coldStorageSf": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.officeBreakdownSfRate": {
            ...constraints.number,
        },
        "listing.attributes.warehouseShellBreakdownRate": {
            ...constraints.number,
        },
        "listing.attributes.blendedRate": {
            ...constraints.number,
        },
        "listing.attributes.ampsNum": {
            ...constraints.number,
        },
        "listing.attributes.phaseNum": {
            ...constraints.number,
        },
        "listing.attributes.gradeLevelDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.dockHighDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.railDoorsNum": {
            ...constraints.integer,
        },
        "listing.attributes.clearHeightMin": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.clearHeightMax": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.cranesQty": {
            ...constraints.wholeNumber,
        },
        "listing.attributes.industrialSf": {
            ...constraints.wholeNumber,
        },
    },
    Retail: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Land: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Multifamily: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    "Life Science": {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Hospitality: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.hospitalitySf": {
            ...constraints.id,
        },
        "listing.attributes.hospitalityUnitCount": {
            ...constraints.id,
        },
        "listing.attributes.roomCount": {
            ...constraints.id,
        },
    },
    Other: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    Educational: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
        "listing.attributes.classroomCount": {
            ...constraints.number,
        },
        "listing.attributes.auditoriumSeatsCount": {
            ...constraints.number,
        },
    },
    Healthcare: {
        "listing.attributes.specialtyType": {
            ...constraints.required,
        },
    },
    listingInformation: {
        "listing.dateAvailable": {
            ...constraints.date,
        },
        "listing.expirationDate": {
            ...constraints.date,
        },
        "listing.leaseExpirationDate": {
            ...constraints.date,
        },
        "listing.attributes.numBldg": {
            ...constraints.number,
        },
        "listing.attributes.lotAcres": {
            ...constraints.number,
        },
        "listing.attributes.lotSf": {
            ...constraints.number,
        },
        "listing.sfAvail": {
            ...constraints.number,
        },
        "listing.sfDivisibleTo": {
            ...constraints.number,
        },
    },
    totalConsideration: {
        commencementDate: {
            ...constraints.required,
            ...constraints.date,
        },
        expirationDate: {
            ...constraints.required,
            ...constraints.date,
        },
        termNumMonths: {
            ...constraints.required,
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.required,
            ...constraints.number,
        },
        escalationRate: {
            ...constraints.required,
            ...constraints.number,
        },
        escalationTerm: {
            ...constraints.required,
            ...constraints.number,
        },
        totalConsiderationAmount: {
            ...constraints.required,
            ...constraints.number,
        },
    },
    leaseDetails: {
        tiRate: {
            ...constraints.number,
            ...constraints.between0And1000,
            ...constraints.required,
        },
        leaseType: {
            ...constraints.required,
        },
        movingAllowanceAmount: {
            ...constraints.number,
        },
        naicsCode: {
            ...constraints.id,
        },
        sicCode: {
            ...constraints.id,
        },
        currentSf: {
            ...constraints.number,
        },
        squareFeetChange: {
            ...constraints.number,
        },
    },
    Lease: {
        //comp information
        //required fields
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.required,
            ...constraints.date,
        },
        renewalFlg: {
            ...constraints.required,
        },
    },
    Sale: {
        //required fields
        "listing.listingName": {
            ...constraints.required,
        },
        transactionDate: {
            ...constraints.required,
            ...constraints.date,
        },
        closedDate: {
            ...constraints.required,
            ...constraints.date,
        },
        totalConsiderationAmount: {
            ...constraints.required,
            ...constraints.number,
        },
        squareFeet: {
            ...constraints.required,
            ...constraints.number,
        },
        //other constraints
    },
    parkingInformation: {
        "listing.attributes.parkingTotal": {
            ...constraints.number,
        },
        "listing.attributes.surfaceParkingSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingGarageSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.parkingReservedSpaceCount": {
            ...constraints.number,
        },
        "listing.attributes.carportParkingSpaceCount": {
            ...constraints.number,
        },
    },
    annualRentCalculationConstraints: {
        ratePerYear: {
            ...constraints.required,
            ...constraints.number,
        },
    },
    montlyRentCalculationConstraints: {
        ratePerSqftPerMonth: {
            ...constraints.required,
            ...constraints.number,
        },
        ratePerSqftPerYear: {
            ...constraints.required,
            ...constraints.number,
        },
        ratePerMonth: {
            ...constraints.required,
            ...constraints.number,
        },
    },
};

const validateOutsideOrAppraisalComp = (comp, useType, transactionType, documents, compType) => {
    let whatToValidate = [];
    switch (transactionType) {
        case "Lease":
            whatToValidate = [
                useType,
                transactionType,
                "leaseDetails",
                "totalConsideration",
                "buildingInformation",
                "parkingInformation",
                "listingFinancial",
                "listingInformation",
            ];
            comp.annualRentForCalcFlg
                ? whatToValidate.push("annualRentCalculationConstraints")
                : whatToValidate.push("montlyRentCalculationConstraints");
            break;
        case "Sale":
            whatToValidate = [
                useType,
                transactionType,
                "buildingInformation",
                "parkingInformation",
                "saleFinancial",
                "listingInformation",
            ];
            break;
        default:
            break;
    }
    let combinedConstraints = {};

    each(whatToValidate, (section) => {
        let sectionConstraints = compConstraints[section];
        if (comp?.isPartiallyComplete) {
            if (section === "totalConsideration") {
                const { escalationRate, escalationTerm, ...constraintsWithoutEscalation } = sectionConstraints;
                sectionConstraints = constraintsWithoutEscalation;
            }
            if (section === "leaseDetails") {
                const { tiRate, ...constraintsWithoutTiRate } = sectionConstraints;
                sectionConstraints = constraintsWithoutTiRate;
            }
        }
        combinedConstraints = { ...combinedConstraints, ...sectionConstraints };
    });

    let errors = {
        ...mapValues(validate(comp, combinedConstraints, { fullMessages: false }), (error) => {
            return error[0];
        }),
        ...listingValidation.validateListingBrokersCompany(get(comp, "listing.listingBrokers")),
        ...listingValidation.validateMixedUses(get(comp, "listing.attributes.mixedUses")),
        ...listingValidation.validatePortfolioProperties(get(comp, "listing.attributes.portfolioProperties")),
        ...listingValidation.validateCondos(get(comp, "listing.attributes.condos")),
    };

    return errors;
};

const validateOutsideOrAppraisalByField = (section, id, value, comp) => {
    const sectionLevelCompSections = [
        "listingInformation",
        "listingFinancial",
        "montlyRentCalculationConstraints",
        "annualRentCalculationConstraints",
        "totalConsideration",
    ];
    if (sectionLevelCompSections.includes(section)) {
        const includeMonthlyRent =
            section === "totalConsideration" && id === "squareFeet" && !comp?.annualRentForCalcFlg;
        const sectionConstraints = {
            ...compConstraints[section],
            ...(includeMonthlyRent && compConstraints.montlyRentCalculationConstraints),
        };
        const sectionErrors = validate(comp, sectionConstraints, { fullMessages: false }) || {};
        return Object.keys(sectionConstraints).map((fullKey) => ({
            fullKey,
            resolved: isEmpty(sectionErrors[fullKey]),
        }));
    }

    const { fullKey, result } = validateFieldWithKey(compConstraints, id, value, section);
    return [{ fullKey, resolved: !!fullKey && isEmpty(result) }];
};

const getPanelsToExpand = (errors, useType, transactionType) => {
    const listingInformationSections = [
        useType,
        "parkingInformation",
        "attributes.condos",
        "attributes.mixedUses",
        "attributes.portfolioProperties",
        "listing.listingBrokers",
        "listingInformation",
    ];
    const listingInformationConstraints = listingInformationSections.reduce(
        (acc, section) => ({ ...acc, ...compConstraints[section] }),
        {}
    );

    const compInformationConstraints = { ...compConstraints[transactionType] };
    const listingInformationKeys = Object.keys(listingInformationConstraints);
    const compInformationKeys = [...Object.keys(compInformationConstraints), "listing.attributes.specialtyType"];
    const totalConsiderationKeys = Object.keys(compConstraints.totalConsideration);
    const leaseDetailsKeys = Object.keys(compConstraints.leaseDetails);

    const panelsToExpand = new Set();
    errors &&
        Object.keys(errors).forEach((key) => {
            if (key !== "listing.attributes.specialtyType" && listingInformationKeys.includes(key)) {
                panelsToExpand.add("listingInformation");
            } else if (compInformationKeys.includes(key)) {
                panelsToExpand.add("compInformation");
            } else if (totalConsiderationKeys.includes(key)) {
                panelsToExpand.add("totalConsideration");
            } else if (leaseDetailsKeys.includes(key)) {
                panelsToExpand.add("leaseDetails");
            }
        });

    return [...panelsToExpand];
};

export default {
    validateOutsideOrAppraisalByField,
    validateOutsideOrAppraisalComp,
    getPanelsToExpand,
};
